'use client'

import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { useCurrencyPreference } from '@/hooks/use-currency-preference'
import { MINUTE_PACKAGES, getPackageMinutesLabel } from '@/server/domain/entities'
import type { PriceCurrency } from '@/server/domain/pricing'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { SalePrice } from '@/components/sale-price'
import { editorialPriceDisplay } from '@/lib/price-display'
import { getBrandAuthHref } from '@/lib/brands'
import { PACK3_CTA, PACK6_STYLE, PACK_STYLES, type PackVariant } from '@/lib/pack-styles'

/** Variante visual del bloque de precios: las paletas de `lib/pack-styles.ts`. */
export type LandingPricingVariant = PackVariant

interface LandingPricingProps {
  locale: string
  variant?: LandingPricingVariant
  authHref?: string
}

/** Los dos packs del catálogo, en el orden en que se pintan. */
const PACKS = [
  { key: 'pack3', packageId: 'package_3min' },
  { key: 'pack6', packageId: 'package_6min' },
] as const

type PackKey = (typeof PACKS)[number]['key']

export function LandingPricing({ locale, variant = 'santa', authHref }: LandingPricingProps) {
  const t = useTranslations('landing.pricing')
  const resolvedAuthHref = authHref ?? getBrandAuthHref(locale)

  // La paleta es la misma que viste el paywall de la marca (`BRAND_PAYWALL`): una sola tabla.
  const { section: sectionClassName, decor, check, pack3Card, pack3Badge } = PACK_STYLES[variant]

  // La divisa la resuelve el hook (política en `server/domain/display-currency`).
  // Antes esto vivía duplicado aquí, con su propia copia de la lista de países.
  const [selectedCurrency] = useCurrencyPreference()

  const priceOf = (pkg: typeof MINUTE_PACKAGES[0]) =>
    selectedCurrency === 'USD' ? pkg.priceUSD : pkg.priceEUR

  /** Estilo de cada tarjeta: el 3 viste la variante, el 6 es siempre el destacado. */
  const styleOf = (key: PackKey) =>
    key === 'pack3'
      ? { card: pack3Card, badge: pack3Badge, cta: PACK3_CTA }
      : PACK6_STYLE

  const cards = PACKS.flatMap(({ key, packageId }) => {
    const pkg = MINUTE_PACKAGES.find((p) => p.id === packageId)
    return pkg ? [{ key, pkg, style: styleOf(key) }] : []
  })

  // Un catálogo incompleto no se pinta a medias (comportamiento previo).
  if (cards.length !== PACKS.length) {
    return null
  }

  return (
      <section 
        id="pricing" 
        className={cn(
          'rounded-3xl p-8 shadow-2xl backdrop-blur-md md:p-12 border',
          sectionClassName
        )}
        style={{ contain: 'layout style' }}
      >
      <div className="mb-12 text-center">
        <h2 className="mb-4 text-5xl font-bold md:text-6xl drop-shadow-lg font-christmas">{t('title')}</h2>
        <p className="font-christmas text-2xl text-white/90 md:text-3xl">
          {t('subtitle')}
        </p>
      </div>

      {/* Sin selector de divisa: la decide el país (ADR-021 fases 3-4). */}
      <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
        {cards.map(({ key, pkg, style }) => (
          <PackCard
            key={key}
            packKey={key}
            minutesLabel={getPackageMinutesLabel(pkg)}
            price={priceOf(pkg)}
            currency={selectedCurrency}
            locale={locale}
            style={style}
            decor={decor}
            check={check}
            authHref={resolvedAuthHref}
          />
        ))}
      </div>

      {/* Footer note */}
      <div className="mt-10 text-center">
        <p className="text-base text-white/80 leading-relaxed max-w-2xl mx-auto whitespace-pre-line">
          {t('footer')}
        </p>
      </div>
    </section>
  )
}

/** Las cuatro ventajas de cada pack, en el orden en que están las traducciones. */
const FEATURE_KEYS = ['feature1', 'feature2', 'feature3', 'feature4'] as const

/** Las tres clases Tailwind que distinguen una tarjeta de la otra. */
interface PackCardStyle {
  card: string
  badge: string
  cta: string
}

/**
 * Tarjeta de un pack. Las dos eran el mismo bloque copiado —mismo esqueleto,
 * distintas clases y claves de traducción— y sostenían sola a `LandingPricing`
 * por encima del umbral de método largo.
 */
function PackCard({
  packKey,
  minutesLabel,
  price,
  currency,
  locale,
  style,
  decor,
  check,
  authHref,
}: {
  packKey: PackKey
  minutesLabel: string
  price: number
  currency: PriceCurrency
  locale: string
  style: PackCardStyle
  decor: string
  /** Color de los checks: sigue a la variante (antes iba `text-emerald-300` fijo, navideño, en todas). */
  check: string
  authHref: string
}) {
  const t = useTranslations('landing.pricing')

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-[2rem] p-8 transition-all hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] border-3 backdrop-blur-sm shadow-[0_8px_32px_rgba(0,0,0,0.3)]',
        style.card
      )}
    >
      {/* Adorno de la variante (copo, destello…) */}
      <div className="absolute top-4 left-4 text-white/20 text-xl">{decor}</div>
      <div className="absolute top-8 right-8 text-white/20 text-sm">{decor}</div>
      <div className="absolute bottom-6 left-6 text-white/20 text-sm">{decor}</div>
      <div className="absolute bottom-12 right-12 text-white/20 text-lg">{decor}</div>

      <div
        className={cn(
          'absolute right-6 top-6 rounded-full px-4 py-1.5 text-sm font-bold shadow-lg z-10',
          style.badge
        )}
      >
        {t(`${packKey}.badge`)}
      </div>

      <div className="relative pt-8">
        <div className="mb-6">
          <h3 className="text-6xl font-bold text-white font-christmas tracking-wide">
            {t(`${packKey}.title`)}
          </h3>
        </div>

        <div className="mb-6">
          <div className="mb-3 flex items-baseline gap-3">
            <span className="text-7xl font-bold text-white drop-shadow-lg">{minutesLabel}</span>
            <span className="text-2xl text-white/90">{t(`${packKey}.minutes`)}</span>
          </div>

          <div className="mb-2">
            <div className="flex items-baseline gap-2 mb-1">
              <SalePrice
                display={editorialPriceDisplay({ price, currency, locale })}
                priceClassName="text-3xl font-bold text-amber-300 drop-shadow-md"
                badgeClassName={style.badge}
              />
            </div>
          </div>
        </div>

        <div className="mb-6 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

        <ul className="space-y-3 text-lg text-white/95 mb-8">
          {FEATURE_KEYS.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <Check className={cn('h-5 w-5 mt-0.5 flex-shrink-0', check)} />
              <span>{t(`${packKey}.${feature}`)}</span>
            </li>
          ))}
        </ul>

        <Link href={authHref}>
          <Button
            size="lg"
            className={cn(
              'font-christmas w-full rounded-full px-6 py-6 text-base font-bold text-white transition-all hover:scale-105 active:scale-95',
              style.cta
            )}
          >
            {t(`${packKey}.cta`)}
          </Button>
        </Link>
      </div>
    </div>
  )
}
